import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/api/client";
import type { LifecycleStateLabel, ProjectRun } from "@/api/projects";

// Tenant-wide runs feed. Same row shape as the project-scoped endpoint
// but with ``project_slug`` populated on every row so the global table
// can render a Project column.
//
// Backend endpoint lands in PR-07 (#144); until then the route 404s
// against the dev server and tests stub fetch.

export interface RunListResponse {
  runs: ProjectRun[];
}

export const RUNS_QUERY_KEY = ["runs", "list"] as const;

export function useRuns() {
  return useQuery({
    queryKey: RUNS_QUERY_KEY,
    queryFn: async (): Promise<ProjectRun[]> => {
      const response = await apiFetch<RunListResponse>("/api/runs");
      return response.runs;
    },
    // Runs change often; short stale matches the project-scoped feed.
    staleTime: 10_000,
  });
}

// Single-run detail. ``Run`` extends ProjectRun with the per-Tile
// invocation rollup the detail page shows. Same wire shape as the
// row in the runs list, plus the dispatched-tile invocations that
// the Flow runner emits at compile time (one row per fanned child;
// for a Tile run, exactly one row).
export interface RunInvocation {
  invocation_id: string;
  tile_name: string;
  node_name: string | null; // null for single-Tile (non-Flow) runs
  status: ProjectRun["status"];
  cache_hit: boolean;
  submitted_at: string;
  // flowstack#1270 — the VCM-reported window and the duration DERIVED from it
  // server-side (`finished - started`, clamped at zero; never stored twice).
  // All three are null together, and null means *not recorded*: see the same
  // fields on `ProjectRun` for why that is never a statement about the run.
  started_at: string | null;
  finished_at: string | null;
  duration_seconds: number | null;
}

export interface Run extends ProjectRun {
  invocations: RunInvocation[];
}

export function runQueryKey(runId: string): readonly unknown[] {
  return ["runs", "detail", runId] as const;
}

/** Lifecycle states the runner is actively progressing through.
 * Mirrors flowstack.lifecycle.state_machine; used to gate polling
 * (per Rupert PR-259 r1: terminal runs must stop polling). */
export const ACTIVE_RUN_STATES: ReadonlySet<LifecycleStateLabel> = new Set([
  "validating",
  "cached",
  "staging_inputs",
  "dispatched",
  "running",
  "collecting_outputs",
  "validating_outputs",
]);

/**
 * Lifecycle states a run can carry an ANALYSIS in.
 *
 * `completed`, plus `failed_scientific` (Rupert PR-935 r0): the materialize
 * route does not gate on lifecycle status at all — it walks whatever
 * `TorsionRefinementResult`/`TorsionScanResult` artifacts the run published —
 * and a run whose SCIENCE was rejected is often the one most worth inspecting.
 * `failed_validation` (nothing ever ran) and `failed_system`/`failed_contract`
 * (infra, not science) stay out: an analysis needs finished science to fit
 * against.
 *
 * Lives here, beside `ACTIVE_RUN_STATES`, because BOTH analysis surfaces read
 * it — the per-project run list and the cross-run comparison's run picker
 * (VIZ U2.1). Two copies of this predicate would let the two lists disagree
 * about which runs are comparable.
 */
export const ANALYZABLE_RUN_STATES: ReadonlySet<LifecycleStateLabel> = new Set([
  "completed",
  "failed_scientific",
]);

export function useRun(runId: string) {
  return useQuery({
    queryKey: runQueryKey(runId),
    queryFn: async (): Promise<Run> => {
      return apiFetch<Run>(`/api/runs/${encodeURIComponent(runId)}`);
    },
    staleTime: 10_000,
    enabled: Boolean(runId),
    // Per Rupert PR-259 r1 (blocker): the docstring promised 5s
    // polling while a run is active but neither useRun nor the run
    // detail page actually wired refetchInterval. Pick 5s while the
    // run is in any active lifecycle state; once terminal, stop —
    // a refetch on a completed/failed run never produces a state
    // change.
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (!status) return false;
      return ACTIVE_RUN_STATES.has(status) ? 5_000 : false;
    },
  });
}
